import { useCallback } from 'react';

import type {
  AiChatSuggestionSource,
  EditingMode,
  SuggestionMarkerPair,
  SuggestionSource,
} from '@aline/common';
import { AlineTransforms, getIsAiChatSource, Queries, SuggestionSourceSchema } from '@aline/common';
import type { Editor, Location } from 'slate';
import { SuggestionStatus } from 'src/components/ai/chat/chat-types';
import { useUpdateSuggestionStatusInChat } from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/CodeSuggestionListElement/handle-suggestions-hooks';
import { useEditorSuggestionState } from 'src/editor/editor-suggestion-state-registry';
import { useCurrentUserId } from 'src/gql';
import type { MessageSuggestionStatus } from 'src/gql/generated/apollo-types';
import { useMaybeEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import { clearStaleSuggestionMarkers } from './clear-stale-suggestion-marker';
import { useSelectedSuggestionUnit, useSuggestionUnitNavigationHooks } from './useNavigationHooks';
import { ClientTransforms } from '../../transforms';

type SuggestionSourceMatcher = (source: SuggestionSource) => boolean;

type SuggestionTransformOptions = {
  at: Location;
  authorId: string;
  editingMode: EditingMode;
  sourceMatch: SuggestionSourceMatcher;
};

type SuggestionTransform = (
  editor: Editor,
  options: SuggestionTransformOptions
) => SuggestionSource[];

/**
 * Pushes the outcome of a local accept/reject/keep action to the server.
 *
 * The Slate transforms that handle suggestions run locally and return the
 * affected sources; this hook groups them by message and fires one
 * `UpdateAgentMessageSuggestions` mutation per message so the chat
 * panel's per-suggestion status stays in sync with the document.
 */
export function useSyncSuggestionStatusToServer() {
  const { updateSuggestionStatusInChat } = useUpdateSuggestionStatusInChat();
  const syncSuggestionStatusToServer = useCallback(
    async (suggestionSources: SuggestionSource[], status: MessageSuggestionStatus) => {
      // Filter out any malformed sources that might slip through due to corrupted document state
      const validSources = suggestionSources.flatMap(source => {
        const parsed = SuggestionSourceSchema.safeParse(source);
        if (!parsed.success || !getIsAiChatSource(parsed.data)) return [];
        return [parsed.data];
      });
      if (!validSources.length) return;

      const messageIdToGroupIds = validSources.reduce(addSourceToMessageGroups, {});

      await Promise.all(
        Object.entries(messageIdToGroupIds).map(async ([messageId, groupIdsSet]) => {
          await updateSuggestionStatusInChat({
            groupIds: Array.from(groupIdsSet),
            messageId,
            status,
          });
        })
      );
    },
    [updateSuggestionStatusInChat]
  );

  return {
    syncSuggestionStatusToServer,
  };
}

/**
 * Returns handlers for the three terminal actions on a pending AI suggestion —
 * accept, reject, and "keep" (dismiss the marker without committing to either
 * side). Each handler runs the corresponding Slate transform on the current
 * selection (or the whole document when `handleAllEnabled` is true and the
 * alert is in "handle all" mode), removes the suggestion marker(s), focuses
 * the editor, advances to the next marker for single-suggestion actions, and
 * syncs the resulting status to the chat panel on the server.
 */
export function useSuggestionActions(options: { handleAllEnabled?: boolean } = {}) {
  const editor = useMaybeEditor();
  const authorId = useCurrentUserId();
  const { editingMode } = useEditingMode();
  const { handleAllEnabled = false } = options;
  const { syncSuggestionStatusToServer } = useSyncSuggestionStatusToServer();
  const { handleAllAiEdits, recomputeSuggestions } = useEditorSuggestionState();
  const { handleNavigateDownAfterRemovingCurrent } = useSuggestionUnitNavigationHooks();
  const { selectedSuggestionUnit } = useSelectedSuggestionUnit();

  const runSuggestionAction = useCallback(
    async (options: {
      isHandleAll: boolean;
      status: MessageSuggestionStatus;
      transform: SuggestionTransform;
    }) => {
      const { isHandleAll, status, transform } = options;
      const selectedSuggestionId = selectedSuggestionUnit?.groupId ?? null;
      if (!authorId || !editingMode || !editor) return;

      const at = getSuggestionActionLocation(editor, isHandleAll);
      if (!at) return;

      const handledSources = transform(editor, {
        at,
        authorId,
        editingMode,
        sourceMatch: source =>
          getDoesSourceMatchAction({ isHandleAll, selectedSuggestionId, source }),
      });
      removeHandledMarkersFromEditor({
        authorId,
        editor,
        handledSources,
        isHandleAll,
        selectedSuggestionId,
      });
      clearStaleSuggestionMarkers(editor);

      ClientTransforms.focus(editor);
      handleNavigateDownAfterRemovingCurrent();
      recomputeSuggestions();
      await syncSuggestionStatusToServer(handledSources, status);
    },
    [
      authorId,
      editingMode,
      editor,
      handleNavigateDownAfterRemovingCurrent,
      recomputeSuggestions,
      selectedSuggestionUnit,
      syncSuggestionStatusToServer,
    ]
  );

  const handleRejectSuggestions = useCallback(async () => {
    await runSuggestionAction({
      isHandleAll: handleAllEnabled && handleAllAiEdits,
      status: SuggestionStatus.Rejected,
      transform: AlineTransforms.rejectSuggestionsWithSource,
    });
  }, [handleAllEnabled, handleAllAiEdits, runSuggestionAction]);

  const handleAcceptSuggestions = useCallback(async () => {
    await runSuggestionAction({
      isHandleAll: handleAllEnabled && handleAllAiEdits,
      status: SuggestionStatus.Accepted,
      transform: AlineTransforms.acceptSuggestionsWithSource,
    });
  }, [handleAllEnabled, handleAllAiEdits, runSuggestionAction]);

  const handleKeepSuggestions = useCallback(async () => {
    await runSuggestionAction({
      isHandleAll: handleAllEnabled && handleAllAiEdits,
      status: SuggestionStatus.Redlined,
      transform: AlineTransforms.removeSourceFromSuggestions,
    });
  }, [handleAllEnabled, handleAllAiEdits, runSuggestionAction]);

  return {
    handleAcceptSuggestions,
    handleKeepSuggestions,
    handleRejectSuggestions,
  };
}

function getSuggestionActionLocation(editor: Editor, isHandleAll: boolean): Location | null {
  if (isHandleAll) return [];

  return editor.selection;
}

function addSourceToMessageGroups(
  messageGroups: Record<string, Set<string>>,
  suggestionSource: Pick<AiChatSuggestionSource, 'groupId' | 'messageId'>
) {
  const existingGroupIds = messageGroups[suggestionSource.messageId] ?? new Set<string>();
  existingGroupIds.add(suggestionSource.groupId);
  messageGroups[suggestionSource.messageId] = existingGroupIds;
  return messageGroups;
}

function getDoesSourceMatchAction(options: {
  isHandleAll: boolean;
  selectedSuggestionId: string | null;
  source: SuggestionSource;
}) {
  const { isHandleAll, selectedSuggestionId, source } = options;
  return isHandleAll || source.groupId === selectedSuggestionId;
}

function removeHandledMarkersFromEditor(options: {
  authorId: string;
  editor: Editor;
  handledSources: SuggestionSource[];
  isHandleAll: boolean;
  selectedSuggestionId: string | null;
}) {
  const { authorId, editor, handledSources, isHandleAll, selectedSuggestionId } = options;
  const handledMarkerIds = getHandledMarkerIds({
    authorId,
    handledSources,
    isHandleAll,
    markers: Queries.getAllSuggestionMarkers(editor),
    selectedSuggestionId,
  });

  // Do in reverse to avoid mutations from one removal affecting later collected entries.
  for (const suggestionId of handledMarkerIds.reverse()) {
    AlineTransforms.removeSuggestionMarkers(editor, suggestionId);
  }

  return handledMarkerIds;
}

function getHandledMarkerIds(options: {
  authorId: string;
  handledSources: SuggestionSource[];
  isHandleAll: boolean;
  markers: SuggestionMarkerPair[];
  selectedSuggestionId: string | null;
}) {
  const { authorId, handledSources, isHandleAll, markers, selectedSuggestionId } = options;
  const handledSuggestionIds = getHandledSuggestionIds(handledSources);
  const shouldRemoveMarker = createHandledMarkerPredicate({
    authorId,
    handledSuggestionIds,
    isHandleAll,
    selectedSuggestionId,
  });

  return markers.filter(shouldRemoveMarker).map(marker => marker.start.suggestionId);
}

function getHandledSuggestionIds(handledSources: SuggestionSource[]) {
  return new Set(handledSources.map(source => source.groupId));
}

function createHandledMarkerPredicate(options: {
  authorId: string;
  handledSuggestionIds: Set<string>;
  isHandleAll: boolean;
  selectedSuggestionId: string | null;
}) {
  const { authorId, handledSuggestionIds, isHandleAll, selectedSuggestionId } = options;

  return (marker: SuggestionMarkerPair) => {
    if (!handledSuggestionIds.has(marker.start.suggestionId)) return false;
    if (!isHandleAll) return marker.start.suggestionId === selectedSuggestionId;

    const markerAuthorId = marker.start.authorId;
    return typeof markerAuthorId === 'string' && markerAuthorId === authorId;
  };
}
